import React from 'react';
import { Box, Typography, IconButton } from '@mui/material';

const Pagination = ({ currentPage, totalPages, onPageChange }) => {
    if (!totalPages || totalPages < 1) return null;

    const renderPageNumbers = () => {
        const pages = [];
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);

        if (endPage - startPage < maxVisible - 1) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }

        // First page + ellipsis
        if (startPage > 1) {
            pages.push(
                <IconButton
                    key={1}
                    onClick={() => onPageChange(1)}
                    sx={{
                        minWidth: 36,
                        height: 36,
                        px: 1.5,
                        backgroundColor: 'transparent',
                        color: 'primary.main',
                        fontWeight: 600,
                        fontSize: '14px',
                        '&:hover': {
                            backgroundColor: 'rgba(25, 118, 210, 0.08)',
                        }
                    }}
                >
                    1
                </IconButton>
            );
            if (startPage > 2) {
                pages.push(
                    <Box key="ellipsis-start" sx={{ px: 1, color: '#999', userSelect: 'none' }}>
                        •••
                    </Box>
                );
            }
        }

        // Visible page range
        for (let i = startPage; i <= endPage; i++) {
            const isActive = currentPage === i;
            pages.push(
                <IconButton
                    key={i}
                    onClick={() => onPageChange(i)}
                    sx={{
                        minWidth: 36,
                        height: 36,
                        px: 1.5,
                        backgroundColor: isActive ? 'primary.main' : 'transparent',
                        color: isActive ? 'white' : 'primary.main',
                        fontWeight: 600,
                        fontSize: '14px',
                        boxShadow: isActive ? '0 2px 8px rgba(25, 118, 210, 0.3)' : 'none',
                        transform: isActive ? 'scale(1.05)' : 'scale(1)',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                            backgroundColor: isActive ? 'primary.dark' : 'rgba(25, 118, 210, 0.08)',
                            transform: 'scale(1.05)',
                        }
                    }}
                >
                    {i}
                </IconButton>
            );
        }

        // Ellipsis + last page
        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                pages.push(
                    <Box key="ellipsis-end" sx={{ px: 1, color: '#999', userSelect: 'none' }}>
                        •••
                    </Box>
                );
            }
            pages.push(
                <IconButton
                    key={totalPages}
                    onClick={() => onPageChange(totalPages)}
                    sx={{
                        minWidth: 36,
                        height: 36,
                        px: 1.5,
                        backgroundColor: 'transparent',
                        color: 'primary.main',
                        fontWeight: 600,
                        fontSize: '14px',
                        '&:hover': {
                            backgroundColor: 'rgba(25, 118, 210, 0.08)',
                        }
                    }}
                >
                    {totalPages}
                </IconButton>
            );
        }

        return pages;
    };

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            mt: 4,
            mb: 3,
            gap: 2
        }}>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                Showing page {currentPage} of {totalPages}
            </Typography>

            <Box sx={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 1,
                padding: '16px 24px',
                backgroundColor: 'white',
                borderRadius: '16px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                border: '1px solid #e0e0e0'
            }}>
                <IconButton
                    onClick={() => onPageChange(1)}
                    disabled={currentPage === 1}
                    sx={{
                        width: 36,
                        height: 36,
                        color: currentPage === 1 ? '#ccc' : 'primary.main',
                        backgroundColor: 'transparent',
                        '&:hover': {
                            backgroundColor: currentPage === 1 ? 'transparent' : 'rgba(25, 118, 210, 0.08)',
                        },
                        '&.Mui-disabled': {
                            color: '#e0e0e0'
                        },
                        fontSize: '18px',
                        fontWeight: 'bold'
                    }}
                    title="First page"
                >
                    ««
                </IconButton>

                <IconButton
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    sx={{
                        width: 36,
                        height: 36,
                        color: currentPage === 1 ? '#ccc' : 'primary.main',
                        backgroundColor: 'transparent',
                        '&:hover': {
                            backgroundColor: currentPage === 1 ? 'transparent' : 'rgba(25, 118, 210, 0.08)',
                        },
                        '&.Mui-disabled': {
                            color: '#e0e0e0'
                        },
                        fontSize: '18px',
                        fontWeight: 'bold'
                    }}
                    title="Previous page"
                >
                    ‹
                </IconButton>

                {renderPageNumbers()}

                <IconButton
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    sx={{
                        width: 36,
                        height: 36,
                        color: currentPage === totalPages ? '#ccc' : 'primary.main',
                        backgroundColor: 'transparent',
                        '&:hover': {
                            backgroundColor: currentPage === totalPages ? 'transparent' : 'rgba(25, 118, 210, 0.08)',
                        },
                        '&.Mui-disabled': {
                            color: '#e0e0e0'
                        },
                        fontSize: '18px',
                        fontWeight: 'bold'
                    }}
                    title="Next page"
                >
                    ›
                </IconButton>

                <IconButton
                    onClick={() => onPageChange(totalPages)}
                    disabled={currentPage === totalPages}
                    sx={{
                        width: 36,
                        height: 36,
                        color: currentPage === totalPages ? '#ccc' : 'primary.main',
                        backgroundColor: 'transparent',
                        '&:hover': {
                            backgroundColor: currentPage === totalPages ? 'transparent' : 'rgba(25, 118, 210, 0.08)',
                        },
                        '&.Mui-disabled': {
                            color: '#e0e0e0'
                        },
                        fontSize: '18px',
                        fontWeight: 'bold'
                    }}
                    title="Last page"
                >
                    »»
                </IconButton>
            </Box>
        </Box>
    );
};

export default Pagination;
